import { useMemo, useState } from "react";
import { Card, Chip, HeatmapOverlay, ProvenanceTag } from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useHeatmap, useReplayDetail, useReplays } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { AigenTag, DemoNote, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";
import { PageSnapshotBackdrop } from "./PageSnapshotBackdrop";

type Mode = "click" | "scroll" | "move";

/** Hand-placed heat points per mode (x,y,weight in 0..1) — sample fallback only. */
const POINTS: Record<Mode, { x: number; y: number; w: number }[]> = {
  click: [
    { x: 0.5, y: 0.26, w: 1 },
    { x: 0.7, y: 0.12, w: 0.5 },
    { x: 0.32, y: 0.58, w: 0.7 },
    { x: 0.62, y: 0.58, w: 0.6 },
    { x: 0.5, y: 0.84, w: 0.45 },
    { x: 0.5, y: 0.45, w: 0.3 },
    { x: 0.2, y: 0.12, w: 0.25 },
    { x: 0.85, y: 0.58, w: 0.2 },
  ],
  scroll: [
    { x: 0.5, y: 0.18, w: 1 },
    { x: 0.5, y: 0.34, w: 0.85 },
    { x: 0.5, y: 0.5, w: 0.6 },
    { x: 0.5, y: 0.64, w: 0.4 },
    { x: 0.5, y: 0.78, w: 0.25 },
    { x: 0.5, y: 0.9, w: 0.15 },
    { x: 0.3, y: 0.42, w: 0.3 },
    { x: 0.7, y: 0.42, w: 0.3 },
  ],
  move: [
    { x: 0.46, y: 0.24, w: 0.8 },
    { x: 0.54, y: 0.3, w: 0.7 },
    { x: 0.4, y: 0.55, w: 0.55 },
    { x: 0.6, y: 0.55, w: 0.55 },
    { x: 0.5, y: 0.42, w: 0.5 },
    { x: 0.68, y: 0.14, w: 0.45 },
    { x: 0.5, y: 0.82, w: 0.35 },
    { x: 0.28, y: 0.66, w: 0.3 },
  ],
};

const COPY: Record<Mode, string> = {
  click: "Where people tap. The hero CTA pulls the most clicks; the footer CTA barely registers.",
  scroll: "How far people scroll. Attention fades fast below the pricing fold.",
  move: "Where cursors hover — a proxy for where attention drifts before a click.",
};

/** A cream wireframe of the landing page the heatmap sits over. */
function PageThumb() {
  return (
    <div className="absolute inset-0 bg-canvas">
      <div className="flex items-center justify-between border-b border-hairline px-5 py-3">
        <span className="text-[12px] font-semibold tracking-tight text-ink-strong">
          aigen<span className="text-accent">.</span>
        </span>
        <span className="flex gap-2">
          <span className="h-2 w-10 rounded-full bg-ink-strong/10" />
          <span className="h-2 w-10 rounded-full bg-ink-strong/10" />
          <span className="h-2 w-12 rounded-full bg-ink-strong/15" />
        </span>
      </div>
      <div className="flex flex-col items-center gap-3 px-6 pt-10 pb-8 text-center">
        <span className="h-3 w-2/3 rounded-full bg-ink-strong/15" />
        <span className="h-3 w-1/2 rounded-full bg-ink-strong/12" />
        <span className="mt-2 h-7 w-28 rounded-lg bg-ink-strong/15" />
      </div>
      <div className="grid grid-cols-3 gap-3 px-6 py-6">
        <span className="h-24 rounded-lg bg-paper-2 dark:bg-white/5" />
        <span className="h-24 rounded-lg bg-paper-2 dark:bg-white/5" />
        <span className="h-24 rounded-lg bg-paper-2 dark:bg-white/5" />
      </div>
      <div className="flex flex-col items-center gap-2 px-6 py-8">
        <span className="h-2.5 w-1/2 rounded-full bg-ink-strong/12" />
        <span className="mt-1 h-6 w-24 rounded-lg bg-ink-strong/12" />
      </div>
    </div>
  );
}

export function Heatmaps() {
  const [mode, setMode] = useState<Mode>("click");
  const { siteId, range, live } = useWorkspace();
  const heatQ = useHeatmap(siteId, range);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: heatQ.isLoading,
    isError: heatQ.isError,
    hasData: !!heatQ.data,
  });

  const isLive = state === "live";
  const seeded = !!heatQ.data?.seeded;

  // Live points are already 0..1 coords; normalize weight by max_weight.
  const livePoints =
    isLive && heatQ.data?.points?.length
      ? heatQ.data.points.map((p) => ({
          x: p.x,
          y: p.y,
          w: heatQ.data!.max_weight > 0 ? p.weight / heatQ.data!.max_weight : 0,
        }))
      : [];

  const points = isLive ? livePoints : POINTS[mode];
  const empty = isLive && livePoints.length === 0;

  // Backdrop: render the customer's ACTUAL page from the newest recorded
  // FullSnapshot. Falls back to the wireframe when nothing has been recorded.
  const replaysQ = useReplays(siteId);
  const newestWeb = (replaysQ.data?.sessions ?? []).find((r) => r.source !== "mobile");
  const detailQ = useReplayDetail(siteId, newestWeb?.session_id, isLive && !!newestWeb);
  const backdropEvents = useMemo(() => {
    const evs = (detailQ.data?.events ?? []) as { type?: number }[];
    // rrweb needs the Meta (4) + FullSnapshot (2) base to render anything.
    return evs.some((e) => e.type === 2) ? evs : [];
  }, [detailQ.data]);
  const backdropViewport = useMemo(() => {
    for (const e of backdropEvents as { type?: number; data?: { width?: number; height?: number } }[]) {
      if (e.type === 4 && e.data?.width && e.data?.height) {
        return { width: e.data.width, height: e.data.height };
      }
    }
    return { width: 1440, height: 900 };
  }, [backdropEvents]);
  const hasBackdrop = isLive && backdropEvents.length > 0;

  if (state === "loading") {
    return (
      <div className="grid gap-4 lg:grid-cols-5">
        <Skeleton className="aspect-[3/4] lg:col-span-3" />
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-[120px]" />
          <Skeleton className="h-[160px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Heatmaps"
        desc="Aggregated attention, overlaid on a snapshot of the page."
        action={
          isLive ? (
            <span className="flex items-center gap-2">
              <AigenTag />
              {seeded && <DemoNote />}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="posthog" />
              <SampleChip />
            </span>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {/* mode toggle is sample-only — the live endpoint returns one aggregate map */}
        {!isLive && (
          <>
            <Chip active={mode === "click"} onClick={() => setMode("click")}>
              Click
            </Chip>
            <Chip active={mode === "scroll"} onClick={() => setMode("scroll")}>
              Scroll
            </Chip>
            <Chip active={mode === "move"} onClick={() => setMode("move")}>
              Move
            </Chip>
          </>
        )}
        <span className="ml-auto font-mono text-[11px] text-subtle">
          {isLive ? `${heatQ.data?.samples ?? 0} samples` : "/pricing"}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="overflow-hidden p-3 lg:col-span-3">
          <div
            className="relative w-full overflow-hidden rounded-card border border-hairline"
            style={
              hasBackdrop
                ? { aspectRatio: `${backdropViewport.width} / ${backdropViewport.height}` }
                : undefined
            }
            data-backdrop={hasBackdrop ? "snapshot" : "wireframe"}
          >
            {hasBackdrop ? (
              <PageSnapshotBackdrop events={backdropEvents} viewport={backdropViewport} />
            ) : (
              <div className="aspect-[3/4]">
                <PageThumb />
              </div>
            )}
            {empty ? (
              <div className="absolute inset-0 grid place-items-center bg-canvas/60">
                <EmptyState title="No heatmap yet" detail="Interactions will paint this overlay as visitors arrive." />
              </div>
            ) : (
              <HeatmapOverlay points={points} />
            )}
          </div>
        </Card>

        <div className="space-y-4 lg:col-span-2">
          <Card className="p-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold capitalize tracking-tight text-ink-strong">
                {isLive ? "Attention map" : `${mode} map`}
              </h3>
              {isLive ? <AigenTag /> : <ProvenanceTag source="posthog" />}
            </div>
            <p className="text-[13px] leading-relaxed text-muted">
              {isLive
                ? "Aggregated interaction intensity for this property. Warmer areas drew more attention."
                : COPY[mode]}
            </p>
          </Card>

          <Card className="p-5">
            <h3 className="mb-3 text-[14px] font-semibold tracking-tight text-ink-strong">
              Reading the map
            </h3>
            <ul className="space-y-2 text-[13px] text-muted">
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: "rgba(184,68,34,0.5)" }} />
                Hot — most interaction
              </li>
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: "rgba(200,146,61,0.32)" }} />
                Warm — moderate
              </li>
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-ink-strong/10" />
                Cool — little or none
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
